import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { 
  CheckCircle, 
  Clock, 
  AlertTriangle, 
  MoreVertical,
  Pencil,
  Trash2,
  ExternalLink,
  CalendarCheck2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { Deadline } from '@/types/deadlines';
import { CATEGORY_LABELS, PRIORITY_LABELS } from '@/types/deadlines';
import { formatDueDate, getDaysUntil, getUrgencyLevel } from '@/services/dateCalculationService';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface DeadlineCardProps {
  deadline: Deadline;
  onEdit?: (deadline: Deadline) => void;
  onDelete?: (deadline: Deadline) => void;
  onComplete?: (deadline: Deadline) => void;
  onViewChat?: (chatId: string) => void;
  compact?: boolean;
}

type UrgencyLevel = 'vencido' | 'hoy' | 'urgente' | 'proximo' | 'normal' | 'completado';

const urgencyBorderColors: Record<UrgencyLevel, string> = {
  vencido: 'border-l-deadline-expired bg-deadline-expired/5',
  hoy: 'border-l-deadline-today bg-deadline-today/5',
  urgente: 'border-l-deadline-urgent bg-deadline-urgent/5',
  proximo: 'border-l-deadline-soon bg-deadline-soon/5',
  normal: 'border-l-deadline-normal bg-deadline-normal/5',
  completado: 'border-l-deadline-completed bg-muted/30',
};

const urgencyTextColors: Record<UrgencyLevel, string> = {
  vencido: 'text-deadline-expired font-medium',
  hoy: 'text-deadline-today font-medium',
  urgente: 'text-deadline-urgent font-medium',
  proximo: 'text-deadline-soon font-medium',
  normal: '',
  completado: 'text-muted-foreground',
};

const priorityStyles: Record<string, string> = {
  alta: 'bg-destructive/10 text-destructive border-destructive/20',
  media: 'bg-deadline-soon/10 text-deadline-soon border-deadline-soon/20',
  baja: 'bg-deadline-normal/10 text-deadline-normal border-deadline-normal/20',
};

export function DeadlineCard({
  deadline,
  onEdit,
  onDelete,
  onComplete,
  onViewChat,
  compact = false,
}: DeadlineCardProps) {
  const daysUntil = getDaysUntil(new Date(deadline.due_date));
  const urgency = getUrgencyLevel(daysUntil, deadline.status) as UrgencyLevel;
  
  // Check if date was adjusted for holidays (original_due_date differs from due_date)
  const wasAdjusted = deadline.original_due_date !== deadline.due_date;

  if (compact) {
    return (
      <div
        className={cn(
          'flex items-center gap-3 p-2 rounded-md border-l-4 transition-colors hover:bg-muted/50',
          urgencyBorderColors[urgency]
        )}
      >
        <div className="flex-1 min-w-0">
          <p className={cn(
            'text-sm font-medium truncate',
            deadline.status === 'completado' && 'line-through text-muted-foreground'
          )}>
            {deadline.title}
          </p>
          <p className="text-xs text-muted-foreground">
            {formatDueDate(deadline.due_date)}
          </p>
        </div>
        
        <Badge variant="outline" className="text-xs shrink-0">
          {CATEGORY_LABELS[deadline.category]}
        </Badge>

        {deadline.status !== 'completado' && onComplete && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={() => onComplete(deadline)}
          >
            <CheckCircle className="h-4 w-4" />
          </Button>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'p-4 rounded-lg border-l-4 border bg-card transition-all hover:shadow-md',
        urgencyBorderColors[urgency]
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {deadline.is_critical && (
              <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />
            )}
            <h4 className={cn(
              'font-medium truncate',
              deadline.status === 'completado' && 'line-through text-muted-foreground'
            )}>
              {deadline.title}
            </h4>
          </div>
          
          {deadline.expediente_number && (
            <p className="text-xs text-muted-foreground mb-1">
              <span className="font-medium">Exp:</span> {deadline.expediente_number}
            </p>
          )}
          
          {deadline.interested_party && (
            <p className="text-xs text-muted-foreground mb-1">
              <span className="font-medium">Interesado:</span> {deadline.interested_party}
            </p>
          )}
          
          {deadline.description && (
            <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
              {deadline.description}
            </p>
          )}
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {deadline.status !== 'completado' && onComplete && (
              <DropdownMenuItem onClick={() => onComplete(deadline)}>
                <CheckCircle className="h-4 w-4 mr-2" />
                Marcar completado
              </DropdownMenuItem>
            )}
            {onEdit && (
              <DropdownMenuItem onClick={() => onEdit(deadline)}>
                <Pencil className="h-4 w-4 mr-2" />
                Editar
              </DropdownMenuItem>
            )}
            {deadline.chat_id && onViewChat && (
              <DropdownMenuItem onClick={() => onViewChat(deadline.chat_id!)}>
                <ExternalLink className="h-4 w-4 mr-2" />
                Ver chat origen
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            {onDelete && (
              <DropdownMenuItem 
                onClick={() => onDelete(deadline)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Eliminar
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="flex items-center gap-2 mt-3 flex-wrap">
        <TooltipProvider>
          <div className="flex items-center gap-1.5 text-sm">
            <Clock className="h-3.5 w-3.5 text-muted-foreground" />
            <span className={urgencyTextColors[urgency]}>
              {formatDueDate(deadline.due_date)}
            </span>
            
            {/* Indicador de fecha ajustada por festivo */}
            {wasAdjusted && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <CalendarCheck2 className="h-3.5 w-3.5 text-holiday cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p className="font-medium">Fecha ajustada</p>
                  <p className="text-xs text-muted-foreground">
                    Fecha original: {format(new Date(deadline.original_due_date), "d 'de' MMMM", { locale: es })}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Ajustada al siguiente día hábil por festivo/fin de semana
                  </p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </TooltipProvider>
        
        <Badge variant="outline" className="text-xs">
          {CATEGORY_LABELS[deadline.category]}
        </Badge>
        
        <Badge variant="outline" className={cn('text-xs', priorityStyles[deadline.priority])}>
          {PRIORITY_LABELS[deadline.priority]}
        </Badge>

        {deadline.legal_reference && (
          <span className="text-xs text-muted-foreground italic">
            {deadline.legal_reference}
          </span>
        )}
      </div>
    </div>
  );
}
